<template>
  <div
    class="min-h-full bg-transparent dark:bg-[#0B1120] pb-28 text-slate-800 dark:text-[#F1F5F9] font-sans antialiased"
  >
    <main
      class="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8"
    >
      <!-- Loading Skeleton -->
      <div
        v-if="isLoading"
        class="space-y-6"
      >
        <div
          class="h-48 w-full animate-pulse rounded-2xl bg-slate-200/80"
        ></div>
        <div
          class="h-96 w-full animate-pulse rounded-2xl bg-slate-200/80"
        ></div>
      </div>

      <!-- Main Content Area -->
      <div
        v-else
        class="space-y-6 sm:space-y-8"
      >
        <!-- Canvas Preview Section (Hidden on mobile < md) -->
        <div class="hidden md:block space-y-3.5">
          <div class="flex items-center justify-between">
            <h3
              class="text-xs font-bold uppercase tracking-wider text-slate-500"
            >
              Live Canvas Preview
            </h3>
            <span
              class="rounded-md bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700"
            >
              Realtime
            </span>
          </div>

          <HeroPreview
            :settings="heroSliderSettings"
            :slides="heroSlides"
            :stats="statsList"
            :highlights="heroHighlightsList"
            :header-meta="headerMeta"
          />

          <!-- Pro Tips Card -->
          <div
            class="rounded-2xl border border-slate-200/80 bg-white p-3.5 sm:p-4 text-xs text-slate-600 shadow-xs"
          >
            <p class="font-bold text-slate-800">Pro Tips:</p>
            <ul
              class="mt-1.5 list-disc space-y-1 pl-4 text-slate-500 text-[11px] sm:text-xs"
            >
              <li>
                Press
                <kbd
                  class="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-slate-700 border border-slate-200"
                >
                  Ctrl + S
                </kbd>
                anywhere to save instantly.
              </li>
              <li>
                Use high-resolution landscape images (1920x1080) for optimal
                visual clarity.
              </li>
              <li>
                Add 3 to 4 quick highlight badges to showcase key features in
                the hero section.
              </li>
            </ul>
          </div>
        </div>

        <!-- SECTION 1: SLIDER CONFIGURATION -->
        <div
          class="rounded-2xl border border-slate-200/80 bg-white p-4 sm:p-6 shadow-xs space-y-6"
        >
          <div
            class="flex items-center justify-between border-b border-slate-100 pb-4"
          >
            <div class="flex items-center space-x-2.5">
              <SlidersIcon class="h-5 w-5 text-indigo-600 flex-shrink-0" />
              <h2 class="text-base font-bold text-slate-900">
                Slider Configuration
              </h2>
            </div>
            <span class="text-xs font-medium text-slate-400">Section 1</span>
          </div>

          <div class="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2">
            <!-- Master Enable Switcher -->
            <div class="space-y-4 sm:col-span-2">
              <div
                class="flex items-center justify-between rounded-xl bg-slate-50 p-3.5 border border-slate-100"
              >
                <div>
                  <p class="text-xs font-bold text-slate-800">
                    Enable Hero Slider
                  </p>
                  <p class="text-[11px] text-slate-500">
                    Show or hide the main hero section completely
                  </p>
                </div>
                <button
                  type="button"
                  class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
                  :class="
                    heroSliderSettings.enabled
                      ? 'bg-indigo-600'
                      : 'bg-slate-300'
                  "
                  @click="
                    heroSliderSettings.enabled = !heroSliderSettings.enabled
                  "
                >
                  <span
                    class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                    :class="
                      heroSliderSettings.enabled
                        ? 'translate-x-5'
                        : 'translate-x-0'
                    "
                  />
                </button>
              </div>

              <!-- Secondary Switches Grid -->
              <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div
                  class="flex items-center justify-between rounded-xl border border-slate-100 p-3 bg-white"
                >
                  <span class="text-xs font-semibold text-slate-700"
                    >Autoplay</span
                  >
                  <button
                    type="button"
                    class="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
                    :class="
                      heroSliderSettings.autoplay
                        ? 'bg-indigo-600'
                        : 'bg-slate-300'
                    "
                    @click="
                      heroSliderSettings.autoplay = !heroSliderSettings.autoplay
                    "
                  >
                    <span
                      class="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                      :class="
                        heroSliderSettings.autoplay
                          ? 'translate-x-4'
                          : 'translate-x-0'
                      "
                    />
                  </button>
                </div>

                <div
                  class="flex items-center justify-between rounded-xl border border-slate-100 p-3 bg-white"
                >
                  <span class="text-xs font-semibold text-slate-700"
                    >Infinite Loop</span
                  >
                  <button
                    type="button"
                    class="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
                    :class="
                      heroSliderSettings.infiniteLoop
                        ? 'bg-indigo-600'
                        : 'bg-slate-300'
                    "
                    @click="
                      heroSliderSettings.infiniteLoop =
                        !heroSliderSettings.infiniteLoop
                    "
                  >
                    <span
                      class="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                      :class="
                        heroSliderSettings.infiniteLoop
                          ? 'translate-x-4'
                          : 'translate-x-0'
                      "
                    />
                  </button>
                </div>

                <div
                  class="flex items-center justify-between rounded-xl border border-slate-100 p-3 bg-white"
                >
                  <span class="text-xs font-semibold text-slate-700"
                    >Show Navigation Dots</span
                  >
                  <button
                    type="button"
                    class="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
                    :class="
                      heroSliderSettings.showDots
                        ? 'bg-indigo-600'
                        : 'bg-slate-300'
                    "
                    @click="
                      heroSliderSettings.showDots = !heroSliderSettings.showDots
                    "
                  >
                    <span
                      class="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                      :class="
                        heroSliderSettings.showDots
                          ? 'translate-x-4'
                          : 'translate-x-0'
                      "
                    />
                  </button>
                </div>

                <div
                  class="flex items-center justify-between rounded-xl border border-slate-100 p-3 bg-white"
                >
                  <span class="text-xs font-semibold text-slate-700"
                    >Show Navigation Arrows</span
                  >
                  <button
                    type="button"
                    class="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
                    :class="
                      heroSliderSettings.showArrows
                        ? 'bg-indigo-600'
                        : 'bg-slate-300'
                    "
                    @click="
                      heroSliderSettings.showArrows =
                        !heroSliderSettings.showArrows
                    "
                  >
                    <span
                      class="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                      :class="
                        heroSliderSettings.showArrows
                          ? 'translate-x-4'
                          : 'translate-x-0'
                      "
                    />
                  </button>
                </div>
              </div>
            </div>

            <!-- Configuration Inputs -->
            <div>
              <label
                class="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-600"
              >
                Autoplay Delay (ms)
              </label>
              <input
                v-model.number="heroSliderSettings.autoplayDelay"
                type="number"
                step="500"
                min="1000"
                class="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 transition focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              />
            </div>

            <div>
              <label
                class="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-600"
              >
                Transition Effect
              </label>
              <select
                v-model="heroSliderSettings.animation"
                class="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 transition focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              >
                <option value="fade">Fade Transition</option>
                <option value="slide">Slide Transition</option>
                <option value="zoom">Zoom Transition</option>
              </select>
            </div>

            <div>
              <label
                class="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-600"
              >
                Hero Section Height
              </label>
              <select
                v-model="heroSliderSettings.heroHeight"
                class="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 transition focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              >
                <option value="Small">Small (450px)</option>
                <option value="Medium">Medium (550px)</option>
                <option value="Large">Large (650px)</option>
                <option value="Fullscreen">Fullscreen (100vh)</option>
              </select>
            </div>

            <div>
              <label
                class="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-600"
              >
                Hero Section Width
              </label>
              <select
                v-model="heroSliderSettings.heroWidth"
                class="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 transition focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              >
                <option value="full">Full Width (100%)</option>
                <option value="contained">Contained (Boxed)</option>
              </select>
            </div>

            <div>
              <label
                class="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-600"
              >
                Hero Layout Style
              </label>
              <select
                v-model="heroSliderSettings.layoutStyle"
                class="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 transition focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              >
                <option value="split">Split Layout (Text & Side Image)</option>
                <option value="background">Background Image Overlay</option>
              </select>
            </div>

            <div>
              <label
                class="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-600"
              >
                Transition Duration (ms)
              </label>
              <input
                v-model.number="heroSliderSettings.transitionDuration"
                type="number"
                step="100"
                min="100"
                max="2000"
                class="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 transition focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              />
            </div>

            <!-- HERO HIGHLIGHT PILLS -->
            <div
              class="mt-2 space-y-4 border-t border-slate-100 pt-6 sm:col-span-2"
            >
              <div class="flex items-center justify-between">
                <div>
                  <h3 class="text-sm font-bold text-slate-800">
                    Hero Highlight Badges
                  </h3>
                  <p class="text-xs text-slate-500">
                    Pill badges shown under main hero text
                  </p>
                </div>
                <button
                  type="button"
                  class="inline-flex items-center space-x-1 rounded-lg bg-indigo-50 px-3 py-1.5 text-xs font-bold text-indigo-600 transition hover:bg-indigo-100 disabled:opacity-50 active:scale-95 cursor-pointer"
                  :disabled="heroHighlightsList.length >= 6"
                  @click="addHighlight"
                >
                  <PlusIcon class="h-3.5 w-3.5" />
                  <span>Add Highlight</span>
                </button>
              </div>

              <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <div
                  v-for="(hl, hIdx) in heroHighlightsList"
                  :key="hIdx"
                  class="relative flex flex-col space-y-2 rounded-xl border border-slate-200 bg-slate-50/50 p-3.5"
                >
                  <div class="flex items-center justify-between">
                    <span
                      class="text-[10px] font-bold uppercase tracking-wider text-slate-400"
                    >
                      Highlight {{ hIdx + 1 }}
                    </span>
                    <button
                      type="button"
                      class="text-rose-500 transition hover:text-rose-700 active:scale-90 cursor-pointer p-0.5"
                      @click="removeHighlight(hIdx)"
                    >
                      <Trash2Icon class="h-3.5 w-3.5" />
                    </button>
                  </div>

                  <div>
                    <label
                      class="mb-0.5 block text-[10px] font-medium text-slate-500"
                    >
                      Icon Class (FontAwesome)
                    </label>
                    <input
                      v-model="hl.icon"
                      type="text"
                      placeholder="e.g. fa-user-graduate"
                      class="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-800 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                    />
                  </div>

                  <div>
                    <label
                      class="mb-0.5 block text-[10px] font-medium text-slate-500"
                    >
                      Badge Text
                    </label>
                    <input
                      v-model="hl.text"
                      type="text"
                      placeholder="e.g. CBSE Curriculum"
                      class="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-800 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Quick Metric Badges -->
            <div
              class="mt-2 space-y-4 border-t border-slate-100 pt-6 sm:col-span-2"
            >
              <div class="flex items-center justify-between">
                <div>
                  <h3 class="text-sm font-bold text-slate-800">
                    Hero Quick Stats
                  </h3>
                  <p class="text-xs text-slate-500">
                    Configure metric badges (up to 4) inside hero
                  </p>
                </div>
                <button
                  type="button"
                  class="inline-flex items-center space-x-1 rounded-lg bg-indigo-50 px-3 py-1.5 text-xs font-bold text-indigo-600 transition hover:bg-indigo-100 disabled:opacity-50 active:scale-95 cursor-pointer"
                  :disabled="statsList.length >= 4"
                  @click="addStat"
                >
                  <PlusIcon class="h-3.5 w-3.5" />
                  <span>Add Metric</span>
                </button>
              </div>

              <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div
                  v-for="(stat, sIdx) in statsList"
                  :key="sIdx"
                  class="relative flex flex-col space-y-2 rounded-xl border border-slate-200 bg-slate-50/50 p-3.5"
                >
                  <div class="flex items-center justify-between">
                    <span
                      class="text-[10px] font-bold uppercase tracking-wider text-slate-400"
                    >
                      Metric {{ sIdx + 1 }}
                    </span>
                    <button
                      type="button"
                      class="text-rose-500 transition hover:text-rose-700 active:scale-90 cursor-pointer p-0.5"
                      @click="removeStat(sIdx)"
                    >
                      <Trash2Icon class="h-3.5 w-3.5" />
                    </button>
                  </div>

                  <div>
                    <label
                      class="mb-0.5 block text-[10px] font-medium text-slate-500"
                      >Value</label
                    >
                    <input
                      v-model="stat.number"
                      type="text"
                      placeholder="e.g. 7+"
                      class="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-bold text-slate-800 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                    />
                  </div>

                  <div>
                    <label
                      class="mb-0.5 block text-[10px] font-medium text-slate-500"
                      >Label</label
                    >
                    <input
                      v-model="stat.label"
                      type="text"
                      placeholder="e.g. Acre Campus"
                      class="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Slides List Card -->
        <div class="space-y-4">
          <div class="flex items-center justify-between">
            <div>
              <h2 class="text-base font-bold text-slate-900">
                Hero Slides ({{ heroSlides.length }})
              </h2>
              <p class="text-xs text-slate-500">
                Manage slide images, titles, content, and ordering
              </p>
            </div>
            <button
              type="button"
              class="inline-flex items-center space-x-1.5 rounded-xl bg-indigo-600 px-3.5 py-2 sm:px-4 sm:py-2.5 text-xs font-bold text-white shadow-md shadow-indigo-600/20 transition hover:bg-indigo-500 active:scale-95 cursor-pointer"
              @click="addSlide"
            >
              <PlusIcon class="h-4 w-4" />
              <span>Add Slide</span>
            </button>
          </div>

          <div class="space-y-4">
            <HeroSlideCard
              v-for="(slide, index) in heroSlides"
              :key="slide._id || index"
              :slide="slide"
              :index="index"
              :total="heroSlides.length"
              :errors="validationErrors"
              @move-up="moveSlide(index, -1)"
              @move-down="moveSlide(index, 1)"
              @duplicate="duplicateSlide(index)"
              @delete="deleteSlide(index)"
            />
          </div>
        </div>
      </div>

      <!-- COMPACT FLOATING ACTION BAR (STICKY TO BOTTOM RIGHT ALWAYS) -->
      <teleport to="body">
        <div
          class="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 lg:right-8 z-[900] max-w-[calc(100vw-2rem)] sm:max-w-lg pointer-events-auto"
        >
          <div
            class="rounded-2xl bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-xl border border-slate-200/90 dark:border-[#263449] p-2.5 sm:p-3 shadow-2xl shadow-slate-900/15 flex items-center justify-between gap-3 sm:gap-4 ring-1 ring-slate-950/5 dark:ring-white/5"
          >
            <!-- Status Indicator (Compact) -->
            <div class="flex items-center gap-2 text-xs font-medium min-w-0">
              <!-- Saving State -->
              <span
                v-if="isSaving"
                class="inline-flex items-center gap-1.5 text-indigo-600 dark:text-blue-400 font-semibold truncate"
              >
                <Loader2Icon
                  class="h-4 w-4 animate-spin shrink-0 text-indigo-600 dark:text-blue-400"
                />
                <span class="truncate">Saving...</span>
              </span>

              <!-- Unsaved Changes Badge -->
              <span
                v-else-if="hasUnsavedChanges"
                class="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 dark:bg-amber-950/50 px-2.5 py-1 border border-amber-200/80 dark:border-amber-800/40 text-amber-700 dark:text-amber-400 font-bold shrink-0"
              >
                <span
                  class="h-2 w-2 rounded-full bg-amber-500 animate-pulse"
                ></span>
                <span>Unsaved</span>
              </span>

              <!-- Saved State -->
              <span
                v-else
                class="inline-flex items-center gap-1.5 text-slate-500 dark:text-[#A6B4C8] truncate"
              >
                <CheckCircleIcon class="h-4 w-4 text-emerald-500 shrink-0" />
                <span class="hidden sm:inline text-xs truncate"
                  >All changes saved</span
                >
              </span>
            </div>

            <!-- Action Buttons -->
            <div class="flex items-center gap-2 shrink-0">
              <button
                type="button"
                class="rounded-xl border border-slate-200 dark:border-[#263449] bg-white dark:bg-[#18243A] px-3.5 py-2 text-xs font-bold text-slate-700 dark:text-[#F1F5F9] hover:bg-slate-100 dark:hover:bg-[#1F2E47] transition active:scale-95 cursor-pointer"
                :disabled="isSaving"
                @click="confirmReset"
              >
                Discard
              </button>

              <!-- Primary Save Action Button -->
              <button
                type="button"
                class="inline-flex items-center justify-center space-x-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 active:scale-95 px-4 sm:px-5 py-2 text-xs font-bold text-white shadow-md shadow-indigo-600/30 transition-all duration-150 disabled:opacity-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                :disabled="isSaving"
                @click="handleSave"
              >
                <Loader2Icon
                  v-if="isSaving"
                  class="h-3.5 w-3.5 animate-spin"
                />
                <SaveIcon
                  v-else
                  class="h-3.5 w-3.5"
                />
                <span class="whitespace-nowrap">{{
                  isSaving ? "Saving" : "Save Configuration"
                }}</span>
              </button>
            </div>
          </div>
        </div>
      </teleport>
    </main>

    <!-- Toast Notification -->
    <Transition name="toast">
      <div
        v-if="toast.show"
        class="fixed bottom-24 right-4 sm:right-6 z-50 flex items-center space-x-2.5 rounded-2xl px-4 py-3 text-xs font-bold text-white shadow-2xl"
        :class="toast.type === 'success' ? 'bg-emerald-600' : 'bg-rose-600'"
      >
        <CheckCircleIcon
          v-if="toast.type === 'success'"
          class="h-4 w-4"
        />
        <AlertCircleIcon
          v-else
          class="h-4 w-4"
        />
        <span>{{ toast.message }}</span>
      </div>
    </Transition>

    <!-- Reset Confirmation Modal -->
    <div
      v-if="showResetModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm"
    >
      <div
        class="w-full max-w-md space-y-4 overflow-hidden rounded-2xl bg-white p-5 sm:p-6 shadow-2xl"
      >
        <h3 class="text-base font-bold text-slate-900">
          Discard Unsaved Changes?
        </h3>
        <p class="text-xs text-slate-500 leading-relaxed">
          Are you sure you want to reset all fields back to their last saved
          state? All unsaved modifications will be lost permanently.
        </p>
        <div class="flex justify-end space-x-2.5 pt-2">
          <button
            type="button"
            class="rounded-xl border border-slate-200 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 active:scale-95 cursor-pointer"
            @click="showResetModal = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-xl bg-rose-600 px-4 py-2 text-xs font-bold text-white shadow-md shadow-rose-600/20 hover:bg-rose-500 active:scale-95 cursor-pointer"
            @click="executeReset"
          >
            Yes, Discard
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, reactive, computed, onMounted, onBeforeUnmount } from "vue";
  import { useStore } from "vuex";
  import {
    Sliders as SlidersIcon,
    Plus as PlusIcon,
    Save as SaveIcon,
    Loader2 as Loader2Icon,
    CheckCircle as CheckCircleIcon,
    AlertCircle as AlertCircleIcon,
    Trash2 as Trash2Icon,
  } from "lucide-vue-next";

  import HeroSlideCard from "@/components/WEB/HeroSlideCard.vue";
  import HeroPreview from "@/components/WEB/HeroPreview.vue";

  const store = useStore();

  const isLoading = ref(true);
  const isSaving = ref(false);
  const showResetModal = ref(false);
  const validationErrors = ref({});

  const headerMeta = reactive({
    establishmentYear: "1985",
    boardAffiliation: "CBSE Affiliated",
    affiliationNumber: "",
    officeHours: "Mon–Sat · 8:00 AM–3:00 PM",
  });

  const heroSliderSettings = reactive({
    enabled: true,
    autoplay: true,
    autoplayDelay: 5000,
    showDots: true,
    showArrows: true,
    infiniteLoop: true,
    animation: "fade",
    heroHeight: "Medium",
    heroWidth: "full",
    layoutStyle: "split",
    transitionDuration: 500,
  });

  const heroHighlightsList = ref([
    { icon: "fa-user-graduate", text: "CBSE Curriculum, Grades I–XII" },
    { icon: "fa-bus", text: "Campus transport across the city" },
    { icon: "fa-shield-heart", text: "CCTV-monitored, secure campus" },
  ]);

  const statsList = ref([
    { number: "7+", label: "Acre Campus" },
    { number: "30+", label: "Activities" },
    { number: "10+", label: "Student Clubs" },
  ]);

  const heroSlides = ref([]);
  const initialStateJson = ref("");

  const toast = reactive({
    show: false,
    message: "",
    type: "success",
  });

  const showToast = (message, type = "success") => {
    toast.show = true;
    toast.message = message;
    toast.type = type;
    setTimeout(() => {
      toast.show = false;
    }, 4000);
  };

  const getComparableImage = (img) => {
    if (img instanceof File) return img.name;
    if (typeof img === "object" && img !== null)
      return img.url || img.secure_url || "";
    return img || "";
  };

  const hasUnsavedChanges = computed(() => {
    const currentState = JSON.stringify({
      settings: heroSliderSettings,
      highlights: heroHighlightsList.value,
      stats: statsList.value,
      slides: heroSlides.value.map((s) => ({
        ...s,
        image: getComparableImage(s.image),
        mobileImage: getComparableImage(s.mobileImage),
      })),
    });
    return currentState !== initialStateJson.value;
  });

  const snapshotState = () => {
    initialStateJson.value = JSON.stringify({
      settings: heroSliderSettings,
      highlights: heroHighlightsList.value,
      stats: statsList.value,
      slides: heroSlides.value.map((s) => ({
        ...s,
        image: getComparableImage(s.image),
        mobileImage: getComparableImage(s.mobileImage),
      })),
    });
  };

  const addHighlight = () => {
    if (heroHighlightsList.value.length >= 6) return;
    heroHighlightsList.value.push({
      icon: "fa-circle-check",
      text: "New Feature Highlight",
    });
  };

  const removeHighlight = (index) => {
    heroHighlightsList.value.splice(index, 1);
  };

  const addStat = () => {
    if (statsList.value.length >= 4) return;
    statsList.value.push({ number: "100+", label: "New Metric" });
  };

  const removeStat = (index) => {
    statsList.value.splice(index, 1);
  };

  const getNewSlideTemplate = () => ({
    image: "",
    mobileImage: "",
    badge: "NEW SESSION 2026-27",
    title: "Admissions Open for Academic Year",
    description:
      "Join Educanium International School for holistic development and modern digital learning infrastructure.",
    caption: "Excellence in Education",
    buttonText: "Apply Online",
    buttonLink: "/admissions",
    buttonTarget: "_self",
    secondaryButtonText: "Explore Campus",
    secondaryButtonLink: "/campus",
    secondaryButtonTarget: "_self",
    overlayOpacity: 0.4,
    contentAlignment: "left",
    sortOrder: heroSlides.value ? heroSlides.value.length : 0,
    active: true,
  });

  const loadData = async () => {
    isLoading.value = true;
    try {
      await store.dispatch("fetchWebsiteDetails");

      const details = store.state.websiteDetails || {};

      headerMeta.establishmentYear =
        details.establishmentYear || details.foundedYear || "1985";
      headerMeta.boardAffiliation =
        details.boardAffiliation || "CBSE Affiliated";
      headerMeta.affiliationNumber = details.affiliationNumber || "";
      headerMeta.officeHours =
        details.officeHours || "Mon–Sat · 8:00 AM–3:00 PM";

      if (details.heroSliderSettings) {
        Object.assign(heroSliderSettings, details.heroSliderSettings);
      }

      if (
        Array.isArray(details.heroHighlights) &&
        details.heroHighlights.length > 0
      ) {
        heroHighlightsList.value = details.heroHighlights.map((h) => ({
          ...h,
        }));
      } else {
        heroHighlightsList.value = [
          { icon: "fa-user-graduate", text: "CBSE Curriculum, Grades I–XII" },
          { icon: "fa-bus", text: "Campus transport across the city" },
          { icon: "fa-shield-heart", text: "CCTV-monitored, secure campus" },
        ];
      }

      if (Array.isArray(details.stats) && details.stats.length > 0) {
        statsList.value = details.stats.map((s) => ({ ...s }));
      } else {
        statsList.value = [
          { number: "7+", label: "Acre Campus" },
          { number: "30+", label: "Activities" },
          { number: "10+", label: "Student Clubs" },
        ];
      }

      if (Array.isArray(details.heroSlides) && details.heroSlides.length > 0) {
        heroSlides.value = details.heroSlides.map((slide) => ({ ...slide }));
      } else {
        heroSlides.value = [getNewSlideTemplate()];
      }

      snapshotState();
    } catch (err) {
      showToast("Failed to load website details", "error");
    } finally {
      isLoading.value = false;
    }
  };

  const addSlide = () => {
    heroSlides.value.push(getNewSlideTemplate());
  };

  const duplicateSlide = (index) => {
    const source = heroSlides.value[index];
    const clone = {
      ...JSON.parse(JSON.stringify(source)),
      _id: undefined,
      title: `${source.title} (Copy)`,
      sortOrder: heroSlides.value.length,
    };
    clone.image = source.image;
    clone.mobileImage = source.mobileImage;
    heroSlides.value.splice(index + 1, 0, clone);
  };

  const deleteSlide = (index) => {
    if (heroSlides.value.length <= 1) {
      showToast("At least one hero slide is required", "error");
      return;
    }
    heroSlides.value.splice(index, 1);
  };

  const moveSlide = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= heroSlides.value.length) return;
    const item = heroSlides.value.splice(index, 1)[0];
    heroSlides.value.splice(targetIndex, 0, item);

    heroSlides.value.forEach((s, i) => {
      s.sortOrder = i;
    });
  };

  const validateForm = () => {
    validationErrors.value = {};
    let isValid = true;

    heroSlides.value.forEach((slide, index) => {
      if (!slide.title || !slide.title.trim()) {
        validationErrors.value[`heroSlides.${index}.title`] =
          "Slide title is required";
        isValid = false;
      }
      if (!slide.image) {
        validationErrors.value[`heroSlides.${index}.image`] =
          "Desktop slide image is required";
        isValid = false;
      }
    });

    return isValid;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showToast("Please fix validation errors before saving", "error");
      return;
    }

    isSaving.value = true;
    try {
      const payload = {
        heroSliderSettings: { ...heroSliderSettings },
        heroHighlights: heroHighlightsList.value.map((hl) => ({
          icon: hl.icon || "fa-circle-check",
          text: hl.text || "",
        })),
        stats: statsList.value.map((st) => ({
          number: st.number || "",
          label: st.label || "",
        })),
        heroSlides: heroSlides.value.map((slide, idx) => ({
          ...slide,
          sortOrder: idx,
        })),
      };

      const response = await store.dispatch("updateWebsiteDetails", payload);
      const updatedSchool = response?.school || store.state.websiteDetails;

      if (updatedSchool) {
        if (Array.isArray(updatedSchool.heroHighlights)) {
          heroHighlightsList.value = updatedSchool.heroHighlights.map((h) => ({
            ...h,
          }));
        }

        if (Array.isArray(updatedSchool.heroSlides)) {
          heroSlides.value = updatedSchool.heroSlides.map((s) => ({
            ...s,
            image: s.image || "",
            mobileImage: s.mobileImage || "",
          }));
        }

        if (Array.isArray(updatedSchool.stats)) {
          statsList.value = updatedSchool.stats.map((s) => ({ ...s }));
        }
      }

      snapshotState();
      showToast("Hero section updated successfully!", "success");
    } catch (err) {
      showToast(err.message || "Error updating website details", "error");
    } finally {
      isSaving.value = false;
    }
  };

  const confirmReset = () => {
    if (hasUnsavedChanges.value) {
      showResetModal.value = true;
    } else {
      executeReset();
    }
  };

  const executeReset = () => {
    showResetModal.value = false;
    loadData();
    showToast("Form reset to saved state", "success");
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "s") {
      e.preventDefault();
      handleSave();
    }
  };

  onMounted(async () => {
    await loadData();
    window.addEventListener("keydown", handleKeyDown);
  });

  onBeforeUnmount(() => {
    window.removeEventListener("keydown", handleKeyDown);
  });
</script>

<style scoped>
  .toast-enter-active,
  .toast-leave-active {
    transition: all 0.3s ease;
  }
  .toast-enter-from,
  .toast-leave-to {
    opacity: 0;
    transform: translateY(1rem);
  }
</style>
